import {finitePosition} from './merfish-scene.js';
import {COLLECTION_CAPS} from './render-guards.js';

const prefixes = tiles => {
  if (!Array.isArray(tiles) || tiles.length > COLLECTION_CAPS.tiles || tiles.some(t => typeof t !== 'string' || !/^[0-7]{0,12}$/.test(t))) throw new Error('Invalid collection tiles');
  return [...new Set(tiles)].sort().filter((t, i, all) => !all.slice(0, i).some(parent => t.startsWith(parent)));
};

/** Atomic viewport snapshots: a parent and its replacement children never coexist.
 * Aggregates are metadata controls, not cells or additional GPU primitives.
 */
export function collectionSnapshot(response, {frame, selectedEntityId} = {}) {
  if ((response[Symbol.for("neuroviz.decodedBytes")] ?? new TextEncoder().encode(JSON.stringify(response)).byteLength) > COLLECTION_CAPS.bytes) throw new Error('Collection delivery exceeds 512 MiB');
  if (response.frame !== frame || !Array.isArray(response.points) || response.points.length > COLLECTION_CAPS.points) throw new Error('Invalid collection frame or point budget');
  const tiles = prefixes((response.tiles || []).map(t => t.tile));
  if (response.failed_tiles?.length) throw new Error('Incomplete collection tiles; retry this viewport');
  const seen = new Set();
  const points = [];
  for (const point of response.points) {
    const position = [point.x, point.y, point.z];
    if (!point.entity_id || !finitePosition(position) || typeof point.leaf !== 'string' || !tiles.some(t => point.leaf.startsWith(t))) throw new Error('Invalid collection point');
    if (seen.has(point.entity_id)) continue;
    seen.add(point.entity_id);
    // Object.assign avoids Babel's property-descriptor spread helper per cell.
    points.push(Object.assign({}, point, {position, coordinate_space_id:frame}));
  }
  return {
    points,
    // Backend extents are [xmin,ymin,zmin,xmax,ymax,zmax], never current-tile extents.
    fullBounds: response.bounds?.length === 6 && response.bounds.every(Number.isFinite) ? [0,1,2].flatMap(i => [response.bounds[i], response.bounds[i+3]]) : null,
    aggregates: (response.tiles || []).filter(t => tiles.includes(t.tile)).map(t => ({tile:t.tile, count:t.matching_count, drill:t.drill === true, shape:'square', label:`Tile ${t.tile || 'root'} · ${t.matching_count} matching observations (not density)`})),
    forcedSelectionCount: seen.has(selectedEntityId) ? response.forced_selection_count || 0 : 0,
  };
}

/** Debounced viewport-only work. Never writes scientific filters or selection.
 * Late transports are fenced even when they ignore AbortSignal.
 */
export function createViewportScheduler({load, cancel = () => {}, commit, onError = () => {}, delay = 180, hysteresis = .08} = {}) {
  let timer, epoch = 0, disposed = false, last = null, requested = null;
  const stop = () => { clearTimeout(timer); epoch++; cancel(); };
  const similar = (a, b) => {
    if (!a || !b || JSON.stringify([a.tiles,a.depth,a.selected]) !== JSON.stringify([b.tiles,b.depth,b.selected])) return false;
    // camera is presentation-only: the server clears it before computing job
    // identity (collection_spatial.create_spatial sets spec["camera"] = {}),
    // so it must never make two otherwise-identical viewports look
    // "different" here. Without this, any camera publish -- including the
    // ResizeObserver-driven re-fit in atlas-layer.js, which fires on every
    // container resize until the user takes the camera, e.g. from iOS
    // Safari's dynamic toolbar reflowing the viewport during ordinary page
    // scroll -- cancels the in-flight/just-finished tile fetch and restarts
    // it, so the view can be perpetually cancelled just short of finishing.
    if (!a.frustum || !b.frustum) {
      const {camera: cameraA, ...restA} = a;
      const {camera: cameraB, ...restB} = b;
      return JSON.stringify(restA) === JSON.stringify(restB);
    }
    return a.frustum.every((v,i) => Math.abs(v-b.frustum[i]) <= Math.max(1e-12, Math.abs(b.frustum[i%3+3]-b.frustum[i%3]))*hysteresis);
  };
  const schedule = (spec, force = false) => {
    if (disposed) return;
    const next = {...spec, tiles:prefixes(spec.tiles)};
    if (!force && similar(next, requested || last)) return;
    stop(); requested = structuredClone(next); const generation = epoch;
    timer = setTimeout(async () => {
      try {
        const data = await load(next);
        if (disposed || generation !== epoch || !data) return;
        commit(data); last = next; requested = null;
      } catch (error) { if (!disposed && generation === epoch) { requested = null; last = null; onError(error); } }
    }, delay);
  };
  return {schedule, retry(){if (requested || last) schedule(requested || last, true);}, dispose(){disposed=true;stop();}};
}

/** Adapter for createCollectionController + one mounted vtk layer. Host owns both. */
export function createCollectionScene({controller, layer, onChange = () => {}, onError = () => {}, delay} = {}) {
  let snapshot = null, viewport = null, identity = null;
  const scheduler = createViewportScheduler({delay, load: spec => controller.loadTiles(spec), cancel: () => controller.cancel('tiles'), onError,
    commit(response) {
      const state = controller.getState().scientific;
      snapshot = collectionSnapshot(response, {frame:state.frame.frame_id, selectedEntityId:state.selectedEntityId});
      layer.update({points:snapshot.points, pointsReady:true, fullBounds:snapshot.fullBounds, selectedEntityId:state.selectedEntityId});
      onChange(snapshot);
    },
  });
  return {
    viewport(spec) {
      const state = controller.getState().scientific;
      const key = JSON.stringify([state.datasetId,state.version,state.collection,state.filters,state.frame]);
      const changed = identity !== key;
      if (changed) {
        identity = key; snapshot = null;
        layer.update({points:[], pointsReady:false, fullBounds:null, merfish:true, coordinateSpaceId:state.frame.frame_id, coordinateUnits:state.frame.units});
        onChange(null);
      }
      viewport = spec;
      scheduler.schedule({...spec, selected:state.selectedEntityId}, changed);
    },
    drill(tile) {
      if (!snapshot?.aggregates.some(t => t.tile === tile && t.drill)) return;
      this.viewport({...viewport, tiles:Array.from({length:8}, (_, i) => tile+i)});
    },
    retry() { if (viewport) scheduler.schedule({...viewport, selected:controller.getState().scientific.selectedEntityId}, true); layer.update({retry:true}); },
    dispose() { scheduler.dispose(); snapshot=null; viewport=null; },
  };
}
